import apiClient from './client';

export interface SessionInfo {
  id: string;
  createdAt: string;
  expiresAt: string;
  isCurrent: boolean;
  deviceType: string | null;
  ipAddress: string | null;
  location: string | null;
}

/**
 * Get all active sessions (refresh tokens) for the current user.
 * The rt_{userId} HttpOnly cookie is read server-side to identify the current session.
 */
export async function getActiveSessions(userId: string): Promise<SessionInfo[]> {
  const response = await apiClient.get<SessionInfo[]>('/auth/sessions', {
    params: { userId },
  });
  return response.data;
}

/**
 * Revoke a specific session by ID.
 */
export async function revokeSession(sessionId: string): Promise<void> {
  await apiClient.delete(`/auth/sessions/${sessionId}`);
}

/**
 * Logout current session — the server reads the rt_{userId} cookie to revoke
 * it from the DB and clears both auth cookies in the response.
 */
export async function logout(userId: string): Promise<void> {
  try {
    await apiClient.post('/auth/logout', null, { params: { userId } });
  } catch (error) {
    // Ignore errors on logout — the cookie may already be expired
    console.warn('Logout request failed:', error);
  }
}

/**
 * Logout from all devices (revoke all refresh tokens).
 */
export async function logoutAll(userId: string): Promise<{ sessionsRevoked: number }> {
  const response = await apiClient.post<{ message: string; sessionsRevoked: number }>(
    '/auth/logout-all',
    null,
    { params: { userId } }
  );
  return response.data;
}
/**
 * Send or resend email verification email.
 */
export async function sendVerificationEmail(email: string): Promise<void> {
  await apiClient.post('/auth/send-verification-email', null, { params: { email } });
}

/**
 * Verify email with token from email link.
 */
export async function verifyEmail(email: string, token: string): Promise<void> {
  await apiClient.post('/auth/verify-email', null, { params: { email, token } });
}

/**
 * Check if user's email is verified.
 */
export async function getEmailStatus(): Promise<{ emailVerified: boolean; email: string }> {
  const response = await apiClient.get<{ emailVerified: boolean; email: string }>('/auth/email-status');
  return response.data;
}

// ==================== Email Change API ====================

/**
 * Change email for an unverified user.
 * The user can directly change their email without additional verification.
 */
export async function changeEmailForUnverified(newEmail: string): Promise<{ message: string; email: string }> {
  const response = await apiClient.post<{ message: string; email: string }>(
    '/auth/change-email',
    null,
    { params: { newEmail } }
  );
  return response.data;
}

/**
 * Request email change for a verified user.
 * Sends a verification code to the current email address.
 */
export async function requestEmailChange(newEmail: string): Promise<{ message: string; pendingEmail: string }> {
  const response = await apiClient.post<{ message: string; pendingEmail: string }>(
    '/auth/request-email-change',
    null,
    { params: { newEmail } }
  );
  return response.data;
}

/**
 * Confirm email change with the code sent to current email.
 */
export async function confirmEmailChange(token: string): Promise<{ message: string; email: string; emailVerified: boolean }> {
  const response = await apiClient.post<{ message: string; email: string; emailVerified: boolean }>(
    '/auth/confirm-email-change',
    null,
    { params: { token } }
  );
  return response.data;
}

/**
 * Cancel a pending email change.
 */
export async function cancelEmailChange(): Promise<void> {
  await apiClient.post('/auth/cancel-email-change');
}

/**
 * Get pending email change status.
 */
export async function getPendingEmailChange(): Promise<{ hasPendingChange: boolean; pendingEmail: string }> {
  const response = await apiClient.get<{ hasPendingChange: boolean; pendingEmail: string }>('/auth/pending-email-change');
  return response.data;
}

// ==================== Password Change API ====================

/**
 * Request a password change verification code.
 * Sends a code to the authenticated user's email.
 */
export async function requestPasswordChange(): Promise<{ message: string }> {
  const response = await apiClient.post<{ message: string }>(
    '/auth/request-password-change'
  );
  return response.data;
}

/**
 * Change password for the authenticated user.
 * Requires the verification token sent via email.
 * Returns updated user info and new auth tokens (keeps current session logged in).
 */
export async function changePassword(token: string, newPassword: string): Promise<{ 
  message: string;
  userId: string;
  email: string;
  fullName: string;
  role: string;
  emailVerified: boolean;
}> {
  const response = await apiClient.post<{ 
    message: string;
    userId: string;
    email: string;
    fullName: string;
    role: string;
    emailVerified: boolean;
  }>(
    '/auth/change-password',
    { token, newPassword }
  );
  return response.data;
}

// ==================== Forgot Password / Reset Password API ====================

/**
 * Request a password reset email.
 * Always returns success to prevent email enumeration.
 */
export async function forgotPassword(email: string): Promise<{ message: string }> {
  const response = await apiClient.post<{ message: string }>(
    '/auth/forgot-password',
    { email }
  );
  return response.data;
}

/**
 * Reset password using the token from email.
 */
export async function resetPassword(email: string, token: string, newPassword: string): Promise<{ message: string }> {
  const response = await apiClient.post<{ message: string }>(
    '/auth/reset-password',
    { email, token, newPassword }
  );
  return response.data;
}

// ==================== Organization Context API ====================

export interface UserOrganization {
  organizationId: string;
  organizationName: string;
  organizationRole: string;
}

/**
 * Get the authenticated user's active organization memberships.
 */
export async function getMyOrganizations(): Promise<UserOrganization[]> {
  // The backend 404s the whole /organizations/** family while the ORGANIZATIONS
  // feature flag is off. "No organizations" is a normal state, not an error, so
  // accept 404 as a resolved response (the shared interceptor console.errors
  // every rejection) and report an empty list.
  const response = await apiClient.get<UserOrganization[]>('/organizations/mine', {
    validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
  });
  if (response.status === 404 || !Array.isArray(response.data)) return [];
  return response.data;
}

/**
 * Switch the active organization context. Reissues the JWT with org claims.
 */
export async function switchOrganization(organizationId: string): Promise<UserOrganization> {
  const response = await apiClient.post<UserOrganization>(
    '/organizations/switch',
    null,
    { params: { organizationId } }
  );
  return response.data;
}

/**
 * Clear the active organization context. Reissues the JWT without org claims.
 */
export async function clearOrganization(): Promise<void> {
  await apiClient.post('/organizations/clear');
}

/**
 * Update the authenticated user's phone number.
 */
export async function updatePhoneNumber(phoneNumber: string, verificationToken?: string): Promise<{ phoneNumber: string }> {
  const response = await apiClient.put<{ phoneNumber: string }>(
    '/profile/phone',
    { phoneNumber, verificationToken }
  );
  return response.data;
}

/**
 * Remove the authenticated user's phone number.
 */
export async function removePhoneNumber(verificationToken?: string): Promise<void> {
  await apiClient.delete('/profile/phone', { data: verificationToken ? { verificationToken } : undefined });
}

/**
 * Send a verification SMS to the authenticated user's phone number.
 */
export async function sendPhoneVerification(): Promise<{ message: string }> {
  const response = await apiClient.post<{ message: string }>('/profile/phone/send-verification');
  return response.data;
}

/**
 * Verify the authenticated user's phone number with a token.
 */
export async function verifyPhone(token: string): Promise<{ message: string }> {
  const response = await apiClient.post<{ message: string }>('/profile/phone/verify', { token });
  return response.data;
}

/**
 * Validate a phone verification token without consuming it (for phone change flow).
 */
export async function validatePhoneToken(token: string): Promise<{ valid: boolean }> {
  const response = await apiClient.post<{ valid: boolean }>('/profile/phone/validate-token', { token });
  return response.data;
}

/**
 * Upload or replace the authenticated user's profile picture.
 */
export async function uploadProfilePicture(file: File): Promise<{ avatarUrl: string }> {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiClient.post<{ avatarUrl: string }>(
    '/profile/avatar',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data;
}

/**
 * Remove the authenticated user's profile picture.
 */
export async function removeProfilePicture(): Promise<void> {
  await apiClient.delete('/profile/avatar');
}
